<template>
    <v-form ref="form" @submit.prevent="save">
        <v-card class="mb-5 pa-2">
            <card-header
                :title="
                    $t('forms.title', {
                        prefix: titlePrefix,
                        resource: $tc('resources.productSale.name', 1),
                    })
                "
            />
            <v-card-text>
                <v-row>
                    <v-col cols="12" xs="12" sm="12" md="4">
                        <v-autocomplete
                            :loading="resellers.loading"
                            :items="resellers.items"
                            :label="
                                $t(
                                    'resources.productSale.attributes.reseller_id',
                                )
                            "
                            item-text="trade_name"
                            item-value="id"
                            :search-input.sync="resellerSearch"
                            v-model="resellerSelected"
                            :error-messages="resellerErrors"
                            @blur="$v.sale.reseller_id.$touch()"
                            return-object
                            no-filter
                            :disabled="sale.charged"
                        ></v-autocomplete>
                    </v-col>

                    <v-col cols="12" xs="12" sm="12" md="4">
                        <v-autocomplete
                            :loading="products.loading"
                            :items="products.items"
                            :label="
                                $t(
                                    'resources.productSale.attributes.product_id',
                                )
                            "
                            item-text="name"
                            item-value="id"
                            :search-input.sync="productSearch"
                            v-model="productSelected"
                            :error-messages="productErrors"
                            @blur="$v.sale.product_id.$touch()"
                            @change="fillSaleWithDefaultOfProduct()"
                            return-object
                            no-filter
                            :disabled="sale.charged"
                        ></v-autocomplete>
                    </v-col>

                    <v-col cols="12" xs="12" sm="12" md="4">
                        <v-select
                            :label="
                                $t(
                                    'resources.productSale.attributes.payment_method',
                                )
                            "
                            :items="paymentMethods"
                            item-text="name"
                            item-value="value"
                            v-model="paymentMethodSelected"
                            :error-messages="paymentMethodErrors"
                            @blur="$v.sale.payment_method.$touch()"
                            return-object
                            :disabled="sale.charged"
                        >
                        </v-select>
                    </v-col>

                    <v-col cols="12" xs="12" sm="12" md="4">
                        <v-currency-field
                            :label="
                                $t('resources.productSale.attributes.price')
                            "
                            v-bind="$t('money-config')"
                            v-model="sale.price"
                            :error-messages="priceErrors"
                            @blur="$v.sale.price.$touch()"
                            :disabled="sale.charged"
                        ></v-currency-field>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="4">
                        <v-text-field
                            :label="
                                $t(
                                    'resources.productSale.attributes.installments',
                                )
                            "
                            :type="'number'"
                            v-model="sale.installments"
                            :error-messages="installmentsErrors"
                            @blur="$v.sale.installments.$touch()"
                            :disabled="sale.charged"
                        ></v-text-field>
                    </v-col>

                    <v-col cols="12" sm="12" md="4">
                        <v-menu
                            ref="firstDueDateMenu"
                            v-model="firstDueDateMenu"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="formattedFirstDueAt"
                                    :label="
                                        $t(
                                            'resources.productSale.attributes.first_due_date',
                                        )
                                    "
                                    prepend-icon="mdi-calendar"
                                    readonly
                                    v-bind="attrs"
                                    v-on="on"
                                    :disabled="sale.charged"
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="sale.first_due_date"
                                no-title
                                scrollable
                                :min="minDueDate"
                                :disabled="sale.charged"
                            />
                        </v-menu>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col class="grey lighten-4 mr-2">
                        <label class="subtitle-2">
                            <v-icon left>mdi-cash-fast</v-icon>Entrada
                        </label>

                        <v-row class="mt-2">
                            <v-col cols="12" xs="12" sm="12" md="4">
                                <v-currency-field
                                    :label="
                                        $t(
                                            'resources.productSale.attributes.down_payment.price',
                                        )
                                    "
                                    v-bind="$t('money-config')"
                                    v-model="downPaymentPrice"
                                    :error-messages="downPaymentPriceErrors"
                                    @blur="$v.sale.down_payment.price.$touch()"
                                    :disabled="sale.charged"
                                ></v-currency-field>
                            </v-col>

                            <v-col cols="12" xs="12" sm="12" md="4">
                                <v-select
                                    :label="
                                        $t(
                                            'resources.productSale.attributes.down_payment.payment_method',
                                        )
                                    "
                                    :items="paymentMethods"
                                    item-text="name"
                                    item-value="value"
                                    v-model="downPaymentMethodSelected"
                                    :error-messages="
                                        downPaymentPaymentMethodErrors
                                    "
                                    @blur="
                                        $v.sale.down_payment.payment_method.$touch()
                                    "
                                    return-object
                                    clearable
                                    :disabled="sale.charged"
                                >
                                </v-select>
                            </v-col>

                            <v-col cols="12" sm="12" md="4">
                                <v-menu
                                    ref="menu"
                                    v-model="dueAtMenu"
                                    :close-on-content-click="true"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="formattedDownPaymentDueAt"
                                            :label="
                                                $t(
                                                    'resources.productSale.attributes.down_payment.due_at',
                                                )
                                            "
                                            prepend-icon="mdi-calendar"
                                            readonly
                                            v-bind="attrs"
                                            v-on="on"
                                            :disabled="sale.charged"
                                        >
                                            <v-icon
                                                slot="append"
                                                @click="downPaymentDueAt = null"
                                                v-if="downPaymentDueAt"
                                            >
                                                mdi-close
                                            </v-icon>
                                        </v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="downPaymentDueAt"
                                        no-title
                                        scrollable
                                        :min="minDownPaymentDueAt"
                                        :disabled="sale.charged"
                                    />
                                </v-menu>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12" xs="12" sm="12" md="12">
                        <v-switch
                            v-model="sale.charge"
                            :hint="$t('resources.productSale.hints.charge')"
                            :disabled="sale.charged"
                        >
                            <template v-slot:label>
                                {{
                                    $t(
                                        'resources.productSale.attributes.charge',
                                    )
                                }}

                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon
                                            small
                                            class="ml-2"
                                            v-bind="attrs"
                                            v-on="on"
                                            >mdi-information</v-icon
                                        >
                                    </template>
                                    <span>{{
                                        $t('resources.productSale.hints.charge')
                                    }}</span>
                                </v-tooltip>
                            </template>
                        </v-switch>
                    </v-col>
                </v-row>
            </v-card-text>
            <form-actions :loadingSave="loading">
                <template #left v-if="!sale.charged">
                    <simulator :sale="sale" />
                </template>
            </form-actions>
        </v-card>
    </v-form>
</template>

<script>
import CardHeader from '@/components/utils/CardHeader'
import FormActions from '@/components/utils/FormActions'
import BaseModel from '@/models/BaseModel'
import Reseller from '@/models/Reseller'
import Product from '@/models/product/Product'
import Simulator from './Simulator'
import { validationMixin } from 'vuelidate'
import {
    required,
    between,
    minValue,
    requiredIf,
} from 'vuelidate/lib/validators'
import moment from 'moment'
import { debounce } from 'lodash'

export default {
    components: {
        CardHeader,
        FormActions,
        Simulator,
    },

    mixins: [validationMixin],

    props: {
        model: {
            type: Object,
            required: true,
            validator: model => {
                return model instanceof BaseModel
            },
        },
        titlePrefix: String,
    },

    data: () => {
        return {
            loading: false,
            resellerSearch: '',
            resellerSelected: null,
            resellers: {
                loading: null,
                items: [],
            },
            productSearch: '',
            productSelected: null,
            products: {
                loading: null,
                items: [],
            },
            paymentMethodSelected: null,
            downPaymentMethodSelected: null,
            paymentMethods: [
                { name: 'Cartão de Crédito', value: 'credit_card' },
                { name: 'Boleto/Pix', value: 'pix_bank_slip' },
            ],
            firstDueDateMenu: false,
            dueAtMenu: false,
            downPaymentPrice: null,
            downPaymentDueAt: null,
            minDueDate: new Date().toISOString().split('T')[0],
            minDownPaymentDueAt: new Date().toISOString().split('T')[0],
        }
    },

    validations() {
        return {
            sale: {
                reseller_id: { required },
                product_id: { required },
                price: { required, minValue: minValue(1) },
                installments: { required, between: between(1, 12) },
                payment_method: { required },
                down_payment: {
                    price: {
                        minValue: minValue(0),
                        requiredIf: requiredIf(() => {
                            return (
                                this.sale.down_payment.price ||
                                this.sale.down_payment.due_at
                            )
                        }),
                    },
                    payment_method: {
                        requiredIf: requiredIf(() => {
                            return (
                                this.sale.down_payment.price ||
                                this.sale.down_payment.due_at
                            )
                        }),
                    },
                    due_at: {
                        requiredIf: requiredIf(() => {
                            return (
                                this.sale.down_payment.price ||
                                this.sale.down_payment.due_at
                            )
                        }),
                    },
                },
            },
        }
    },

    watch: {
        resellerSearch: debounce(function(val) {
            if (val && val !== this.sale.reseller_name) {
                this.loadResellers()
            }
        }, 300),

        productSearch: debounce(function(val) {
            if (val && val !== this.sale.product_name) {
                this.loadProducts()
            }
        }, 300),

        resellerSelected(val) {
            if (val) {
                this.sale.reseller_id = val.id
            } else {
                this.sale.reseller_id = null
            }
        },

        productSelected(val) {
            if (val) {
                this.sale.product_id = val.id
            } else {
                this.sale.product_id = null
            }
        },

        paymentMethodSelected(val) {
            if (val) {
                this.sale.payment_method = val.value
                this.setMinDate('minDueDate', val.value)
            } else {
                this.sale.payment_method = null
            }
        },

        downPaymentMethodSelected(val) {
            if (val) {
                this.sale.down_payment.payment_method =
                    typeof val === 'object' ? val.value : val
                this.setMinDate('minDownPaymentDueAt', val.value)
            } else {
                this.sale.down_payment.payment_method = null
            }
        },

        downPaymentPrice(val) {
            this.sale.down_payment.price = val
        },

        downPaymentDueAt(val) {
            if (val) {
                this.sale.down_payment.due_at = val
            } else {
                this.sale.down_payment.due_at = undefined
            }
        },
    },

    computed: {
        sale() {
            return this.model
        },

        resellerErrors() {
            const errors = []
            if (!this.$v.sale.reseller_id.$dirty) return errors

            !this.$v.sale.reseller_id.required &&
                errors.push(
                    this.$t('forms.errors.required', {
                        label: this.$t(
                            'resources.productSale.attributes.reseller_id',
                        ),
                    }),
                )

            return errors
        },

        productErrors() {
            const errors = []

            if (!this.$v.sale.product_id.$dirty) return errors

            !this.$v.sale.product_id.required &&
                errors.push(
                    this.$t('forms.errors.required', {
                        label: this.$t(
                            'resources.productSale.attributes.product_id',
                        ),
                    }),
                )

            return errors
        },

        priceErrors() {
            const errors = []
            if (!this.$v.sale.price.$dirty) return errors

            !this.$v.sale.price.required &&
                errors.push(
                    this.$t('forms.errors.required', {
                        label: this.$t(
                            'resources.productSale.attributes.price',
                        ),
                    }),
                )

            !this.$v.sale.price.minValue &&
                errors.push(
                    this.$t('forms.errors.minValue', {
                        label: this.$t(
                            'resources.productSale.attributes.price',
                        ),
                        min: 1,
                    }),
                )

            return errors
        },

        installmentsErrors() {
            const errors = []
            if (!this.$v.sale.installments.$dirty) return errors

            !this.$v.sale.installments.required &&
                errors.push(
                    this.$t('forms.errors.required', {
                        label: this.$t(
                            'resources.product.attributes.installments',
                        ),
                    }),
                )

            !this.$v.sale.installments.between &&
                errors.push(
                    this.$t('forms.errors.between', {
                        label: this.$t(
                            'resources.productSale.attributes.installments',
                        ),
                        min: 1,
                        max: 12,
                    }),
                )

            return errors
        },

        paymentMethodErrors() {
            const errors = []
            if (!this.$v.sale.payment_method.$dirty) return errors

            !this.$v.sale.payment_method.required &&
                errors.push(
                    this.$t('forms.errors.required', {
                        label: this.$t(
                            'resources.productSale.attributes.payment_method',
                        ),
                    }),
                )

            return errors
        },

        downPaymentPriceErrors() {
            const errors = []
            if (!this.$v.sale.down_payment.price.$dirty) return errors

            !this.$v.sale.down_payment.price.requiredIf &&
                errors.push(
                    this.$t('forms.errors.requiredIf', {
                        label: this.$t(
                            'resources.productSale.attributes.down_payment.price',
                        ),
                    }),
                )

            !this.$v.sale.down_payment.price.minValue &&
                errors.push(
                    this.$t('forms.errors.minValue', {
                        label: this.$t(
                            'resources.productSale.attributes.down_payment.price',
                        ),
                        min: 1,
                    }),
                )

            return errors
        },

        downPaymentPaymentMethodErrors() {
            const errors = []
            if (!this.$v.sale.down_payment.payment_method.$dirty) return errors

            !this.$v.sale.down_payment.payment_method.requiredIf &&
                errors.push(
                    this.$t('forms.errors.requiredIf', {
                        label: this.$t(
                            'resources.productSale.attributes.down_payment.payment_method',
                        ),
                    }),
                )

            return errors
        },

        downPaymentDueAtErrors() {
            const errors = []
            if (!this.$v.sale.down_payment.due_at.$dirty) return errors

            !this.$v.sale.down_payment.due_at.requiredIf &&
                errors.push(
                    this.$t('forms.errors.requiredIf', {
                        label: this.$t(
                            'resources.productSale.attributes.down_payment.due_at',
                        ),
                    }),
                )

            return errors
        },

        formattedFirstDueAt() {
            if (this.sale.first_due_date) {
                return moment(this.sale.first_due_date).format('DD/MM/YYYY')
            }

            return null
        },

        formattedDownPaymentDueAt() {
            if (this.downPaymentDueAt) {
                return moment(this.downPaymentDueAt).format('DD/MM/YYYY')
            }

            return null
        },
    },

    methods: {
        setMinDate(field, payment_method) {
            let minDueDate = new Date()

            if (payment_method == 'bank_slip') {
                minDueDate.setDate(minDueDate.getDate() + 3)
            }

            this[field] = minDueDate.toISOString().split('T')[0]
        },

        fillSaleWithDefaultOfProduct() {
            if (this.productSelected) {
                this.sale.price = this.productSelected.price
                this.sale.installments = this.productSelected.installments
            }
        },

        async setupResellers() {
            this.resellers.loading = true
            this.loadResellers().then(() => {
                if (this.sale.reseller) {
                    this.resellers.items.push(this.sale.reseller)
                    this.resellerSelected = this.sale.reseller
                }

                this.resellers.loading = false
            })
        },

        async loadResellers() {
            let reseller = new Reseller()

            try {
                let res
                if (this.resellerSearch) {
                    res = await reseller
                        .where('search', this.resellerSearch)
                        .all()
                } else {
                    res = await reseller.where('active', true).all()
                }

                this.resellers.items = res.data
            } catch (e) {
                this.$bus.$emit(
                    'notify',
                    'error',
                    this.$t('resources.feedbacks.errors.loadingList', {
                        resource: this.$tc(
                            'resources.reseller.name',
                            2,
                        ).toLowerCase(),
                    }),
                )
            }
        },

        async setupProducts() {
            this.products.loading = true
            this.loadProducts().then(() => {
                if (this.sale.product) {
                    this.products.items.push(this.sale.product)
                    this.productSelected = this.sale.product
                }

                this.products.loading = false
            })
        },

        async loadProducts() {
            this.products.loading = true
            let product = new Product()
            try {
                let res
                if (this.productSearch) {
                    res = await product
                        .where('search', this.productSearch)
                        .all()
                } else {
                    res = await product.all()
                }

                this.products.loading = false
                this.products.items = res.data
            } catch (e) {
                this.$bus.$emit(
                    'notify',
                    'error',
                    this.$t('resources.feedbacks.errors.loadingList', {
                        resource: this.$tc(
                            'resources.productSale.name',
                            2,
                        ).toLowerCase(),
                    }),
                )
            }
        },

        async setupPaymentMethods() {
            this.paymentMethodSelected = this.paymentMethods.find(
                paymentMethod => {
                    return paymentMethod.value == this.sale.payment_method
                },
            )
        },

        async setupDownPayment() {
            this.sale.down_payment = {}

            if (this.sale.id) {
                let downPayment = this.sale.productSalePeriod.data.find(
                    element => element.installment == 0,
                )

                this.downPaymentPrice = downPayment.price
                this.downPaymentMethodSelected = downPayment.payment_method
                this.downPaymentDueAt = downPayment.due_at
            }
        },

        async save() {
            this.loading = true
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$bus.$emit(
                    'notify',
                    'error',
                    this.$t('form.messages.validation-error'),
                )

                this.loading = false
                return
            }

            let clone = this.model.clone()
            let res = null

            try {
                res = await clone.save().catch(() => {})
            } finally {
                this.loading = false
            }

            if (!res) return
            this.$emit('saved')

            this.$bus.$emit(
                'notify',
                'success',
                this.$t('forms.messages.saved', {
                    resource: this.$tc(
                        'resources.productSale.name',
                        1,
                    ).toLowerCase(),
                }),
            )
        },
    },

    created() {
        if (this.sale.charged) {
            this.sale.charge = true
        }

        this.paymentMethodSelected = this.sale.payment_method

        Promise.all([
            this.setupResellers(),
            this.setupProducts(),
            this.setupPaymentMethods(),
            this.setupDownPayment(),
        ])
    },
}
</script>

<style></style>
